import React, { useState, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useScrollTrigger,
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(10px)',
  borderBottom: '1px solid rgba(220, 20, 60, 0.1)',
  boxShadow: 'none',
}));

const NavButton = styled(Button)(({ theme, active }) => ({
  color: active ? '#dc143c' : '#3a3532',
  marginLeft: theme.spacing(2),
  textTransform: 'none',
  fontWeight: 500,
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    width: active ? '100%' : '0%',
    height: '2px',
    bottom: '0',
    left: '0',
    backgroundColor: '#dc143c',
    transition: 'width 0.3s ease-in-out',
  },
  '&:hover::after': {
    width: '100%',
  },
}));

const Logo = styled('img')({
  height: 40,
  marginRight: 8,
});

function HideOnScroll({ children }) {
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const Navbar = ({ hasResults, onClearResults }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navItems = [
    { text: 'Home', path: '/' },
    { text: 'Upload Files', path: '/upload' },
  ];

  const drawer = (
    <Box sx={{ textAlign: 'center' }}>
      <Box 
        sx={{ my: 2 }}
        onClick={(e) => {
          if (hasResults) {
            e.preventDefault();
            setPendingNavigation("/");
            setShowWarning(true);
          }
          handleDrawerToggle();
        }}
        component={Link}
        to="/"
      >
        <Logo src="/Image/Logo.png" alt="Logo" />
      </Box>
      <List>
        {navItems.map((item) => (
          <ListItem 
            key={item.text}
            onClick={(e) => {
              if (hasResults && location.pathname === '/upload') {
                e.preventDefault();
                setPendingNavigation(item.path);
                setShowWarning(true);
              }
            }}
            component={Link}
            to={item.path}
            sx={{
              color: location.pathname === item.path ? '#dc143c' : '#3a3532',
              textAlign: 'center',
              textDecoration: 'none',
              cursor: 'pointer'
            }}
          >
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <HideOnScroll>
        <StyledAppBar position="fixed">
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ 
                mr: 2, 
                display: { sm: 'none' },
                color: '#3a3532',
              }}
            >
              <MenuIcon />
            </IconButton>
            
            <Box
              onClick={(e) => {
                if (hasResults) {
                  e.preventDefault();
                  setPendingNavigation("/");
                  setShowWarning(true);
                }
              }}
              component={Link}
              to="/"
              style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
            >
              {/* <Logo src="/Image/exp2.png" alt="Logo" /> */}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#dc143c',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                ResumeX
              </Typography>
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              {navItems.map((item) => (
                <NavButton
                  key={item.text}
                  onClick={(e) => {
                    if (hasResults && location.pathname === '/upload') {
                      e.preventDefault();
                      setPendingNavigation(item.path);
                      setShowWarning(true);
                    }
                  }}
                  component={Link}
                  to={item.path}
                  active={location.pathname === item.path ? 1 : 0}
                >
                  {item.text}
                </NavButton>
              ))}
            </Box>
          </Toolbar>
        </StyledAppBar>
      </HideOnScroll>
      
      <Box component="nav">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 240,
              backgroundColor: 'background.default',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      <Toolbar /> {/* Spacer */}
      
      {/* Warning Dialog */}
      <Dialog
        open={showWarning}
        onClose={() => setShowWarning(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Caution: This Action Will Clear Results"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Navigating away will clear the current analysis results. This action cannot be undone. Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowWarning(false)}
            sx={{ color: '#3a3532' }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setShowWarning(false);
              if (onClearResults) {
                onClearResults();
              }
              if (pendingNavigation) {
                window.location.href = pendingNavigation;
              }
            }}
            sx={{
              backgroundColor: '#dc143c',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#b01030',
              }
            }}
            autoFocus
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Navbar;
